import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Receipt, Send, Copy, CheckCircle2, XCircle, Plus, Trash2, DollarSign, Download, Search, Car, User, Sparkles } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import { api as PUBLIC_API } from "../lib/api";
import { promoActive } from "../lib/promo";
import PageHeader from "./components/PageHeader";
import PpfUpgradesPanel from "./components/PpfUpgradesPanel";
import { buildQuoteText, copyText } from "./quoteText";
import { SearchSelect, NewCustomerForm, NewVehicleForm } from "./components/QuickPick";

const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm";

// Branded PDF download (blob → file)
async function downloadPdf(kind, id, filename) {
  try {
    const { data } = await CRM_API.get(`/${kind}/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename}.pdf saved`);
  } catch (e) { toast.error(errMsg(e)); }
}

const EST_STATUS = {
  draft: "bg-white/15 text-white/70", sent: "bg-blue-500/25 text-blue-300",
  viewed: "bg-purple-500/25 text-purple-300", approved: "bg-emerald-500/25 text-emerald-300",
  declined: "bg-red-500/25 text-red-300", expired: "bg-white/10 text-white/40",
};
const INV_STATUS = {
  draft: "bg-white/15 text-white/70", sent: "bg-blue-500/25 text-blue-300",
  partial: "bg-amber-500/25 text-amber-300", paid: "bg-emerald-500/25 text-emerald-300",
  overdue: "bg-red-500/25 text-red-300", cancelled: "bg-white/10 text-white/40",
};

/* ==================== SHARED CREATE FORM ==================== */
function DocCreateModal({ kind, prefillCustomerId = "", prefillVehicleId = "", prefillServiceIds = [], prefillWorkOrderId = "", onClose, onSaved }) {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [customerId, setCustomerId] = useState(prefillCustomerId);
  const [vehicleId, setVehicleId] = useState(prefillVehicleId);
  const [custVehicles, setCustVehicles] = useState([]);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [svcQuery, setSvcQuery] = useState("");
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState("0");
  const [busy, setBusy] = useState(false);
  const prefilledRef = React.useRef(false);
  const [pricingCfg, setPricingCfg] = useState(null);
  const [ppfCfg, setPpfCfg] = useState(null);
  const [vclass, setVclass] = useState(null);

  useEffect(() => {
    PUBLIC_API.get("/pricing/config").then(({ data }) => setPricingCfg(data)).catch(() => {});
    PUBLIC_API.get("/ppf/config").then(({ data }) => setPpfCfg(data)).catch(() => {});
  }, []);

  // Class-based pricing: classify the selected vehicle (website D1–D5 engine)
  useEffect(() => {
    setVclass(null);
    const v = custVehicles.find((x) => x.id === vehicleId);
    if (!v?.year || !v?.make || !v?.model) return;
    PUBLIC_API.post("/vehicles/classify", { year: Number(v.year), make: v.make, model: v.model })
      .then(({ data }) => setVclass(data?.vehicle_class || null))
      .catch(() => {});
  }, [vehicleId, custVehicles]);

  // Website-catalog price for a synced service: class price when the vehicle
  // is known, else flat / cheapest option / internal base price.
  const svcPrice = (s) => {
    // PPF packages / windshield film / individual panels — from the PPF engine
    if (s.pricing_key?.startsWith("ppf-") && ppfCfg) {
      if (s.pricing_key === "ppf-windshield") {
        if (vclass && ppfCfg.windshield?.prices?.[vclass] != null) return ppfCfg.windshield.prices[vclass];
        const vals = Object.values(ppfCfg.windshield?.prices || {}).filter((v) => v != null);
        return vals.length ? Math.min(...vals) : (s.price ?? 0);
      }
      if (s.pricing_key.startsWith("ppf-part:")) {
        const key = s.pricing_key.replace("ppf-part:", "");
        const part = (ppfCfg.parts || []).find((x) => x.key === key);
        if (part) {
          const base = part.kind === "flat" ? part.price : part.kind === "sided" ? part.pair : part.all4;
          const mults = ppfCfg.part_class_multipliers || {};
          const mult = vclass && part.class_scaled ? (mults[vclass] || 1) : 1;
          return mult !== 1 ? Math.round((base * mult) / 5) * 5 : base;
        }
      }
      const key = s.pricing_key.replace("ppf-pkg:", "");
      const p = (ppfCfg.packages || []).find((x) => x.key === key);
      if (p?.prices) {
        if (vclass && p.prices[vclass] != null) return p.prices[vclass];
        return Math.min(...Object.values(p.prices));
      }
    }
    if (s.pricing_key && pricingCfg) {
      const ps = (pricingCfg.services || []).find((x) => x.key === s.pricing_key);
      if (ps) {
        if (vclass && ps.prices?.[vclass] != null) return ps.prices[vclass];
        if (ps.options?.length) return Math.min(...ps.options.map((o) => o.price));
        if (ps.flat_price != null) return ps.flat_price;
        const vals = Object.values(ps.prices || {}).filter((v) => v != null);
        if (vals.length) return Math.min(...vals);
      }
    }
    return s.price ?? 0;
  };

  useEffect(() => {
    if (prefillCustomerId) CRM_API.get(`/customers/${prefillCustomerId}`).then(({ data }) => setCustomer(data.customer)).catch(() => {});
    CRM_API.get("/services").then(({ data }) => {
      const list = (Array.isArray(data) ? data : []).filter((s) => s.active !== false);
      setServices(list);
      // Work-order bridge: prefill line items from the WO's services (once)
      if (!prefilledRef.current && prefillServiceIds.length) {
        prefilledRef.current = true;
        const pre = prefillServiceIds
          .map((id) => list.find((s) => s.id === id))
          .filter(Boolean)
          .map((s) => ({ service_id: s.id, name: s.name, qty: 1, unit_price: s.price ?? 0, discount: 0 }));
        if (pre.length) setItems(pre);
      }
    });
  }, []);

  // Vehicles of the chosen customer; auto-select when there is exactly one
  useEffect(() => {
    if (!customerId) { setCustVehicles([]); return; }
    CRM_API.get(`/vehicles?customer_id=${customerId}&limit=200`).then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setCustVehicles(list);
      setVehicleId((cur) => {
        if (list.length === 1) return list[0].id;
        if (cur && list.some((v) => v.id === cur)) return cur;
        return "";
      });
    }).catch(() => {});
  }, [customerId]);

  const svcGroups = useMemo(() => {
    const q = svcQuery.trim().toLowerCase();
    const list = q ? services.filter((s) => s.name.toLowerCase().includes(q)) : services;
    const groups = {};
    list.forEach((s) => { (groups[s.category || "Other"] = groups[s.category || "Other"] || []).push(s); });
    return groups;
  }, [services, svcQuery]);

  const setItem = (i, k, v) => setItems((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const addService = (s) => {
    setItems((rows) => {
      const i = rows.findIndex((r) => r.service_id === s.id);
      if (i >= 0) return rows.map((r, j) => (j === i ? { ...r, qty: Number(r.qty) + 1 } : r));
      return [...rows, { service_id: s.id, name: s.name, qty: 1, unit_price: svcPrice(s), discount: 0 }];
    });
  };

  // One-click package: adds all bundle services at class prices and applies
  // the same discount the website would give right now — Grand Opening promo
  // while it's live, otherwise the package's own discount percent.
  const addBundle = (b) => {
    const rows = [];
    for (const key of b.services || []) {
      const s = services.find((x) => x.pricing_key === key);
      if (s) rows.push({ service_id: s.id, name: s.name, qty: 1, unit_price: svcPrice(s), discount: 0, pkey: s.pricing_key });
    }
    if (!rows.length) { toast.error("Package services not found in the catalog"); return; }
    const promo = pricingCfg?.promo;
    const promoOn = promoActive(promo);
    let saved = 0;
    if (promoOn) {
      const main = Number(promo.percent) || 50;
      const add = Number(promo.multi_percent) || 20;
      const svcPcts = promo.service_percents || {};
      [...rows.keys()].sort((a, b) => rows[b].unit_price - rows[a].unit_price).forEach((i, rank) => {
        const pct = Number(svcPcts[rows[i].pkey]) || (rank === 0 ? main : add);
        rows[i].discount = Math.floor(rows[i].unit_price * pct / 100);
        saved += rows[i].discount;
      });
    } else {
      const pct = Number(b.discount_percent) || 10;
      rows.forEach((r) => { r.discount = Math.floor(r.unit_price * pct / 100); saved += r.discount; });
    }
    setItems((cur) => {
      const next = [...cur];
      for (const r of rows) {
        const i = next.findIndex((x) => x.service_id === r.service_id);
        if (i >= 0) next[i] = { ...next[i], qty: Number(next[i].qty) + 1 };
        else next.push(r);
      }
      return next;
    });
    toast.success(`${b.name}: ${rows.length} service${rows.length > 1 ? "s" : ""} added — saved $${saved}${promoOn ? " (Grand Opening)" : ` (package −${b.discount_percent || 10}%)`}`);
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((acc, it) => acc + Math.max(0, Number(it.qty || 1) * Number(it.unit_price || 0) - Number(it.discount || 0)), 0);
    const after = Math.max(0, subtotal - (Number(discount) || 0));
    const tax = Math.round(after * 5) / 100;
    return { subtotal, tax, total: after + tax };
  }, [items, discount]);

  const save = async () => {
    const valid = items.filter((i) => i.name.trim() && Number(i.unit_price) > 0);
    if (!customerId || !vehicleId) { toast.error("Select customer and vehicle"); return; }
    if (!valid.length) { toast.error("Add at least one service with a price"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        vehicle_id: vehicleId,
        work_order_id: prefillWorkOrderId || null,
        items: valid.map((i) => ({
          service_id: i.service_id || null,
          name: i.name.trim(), qty: Number(i.qty) || 1,
          unit_price: Number(i.unit_price), discount: Number(i.discount) || 0,
        })),
        discount_total: Number(discount) || 0,
      };
      const { data } = await CRM_API.post(kind === "estimate" ? "/estimates" : "/invoices", payload);
      toast.success(`${kind === "estimate" ? data.est_number : data.invoice_number} created`);
      onSaved();
      nav(kind === "estimate" ? `/crm/estimates/${data.id}` : `/crm/invoices/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={`${kind}-create-modal`}>
      <div className="w-full max-w-4xl rounded-2xl bg-[#121216] border border-white/12 h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-white/10 shrink-0">
          <span className="w-6 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">
            New {kind === "estimate" ? "Estimate" : "Invoice"}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
          {/* LEFT: customer + vehicle + service picker */}
          <div className="p-6 lg:border-r border-white/10 space-y-5 lg:overflow-y-auto lg:min-h-0">
            {/* customer */}
            <div>
              <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest mb-2 flex items-center gap-1.5"><User className="w-3 h-3" /> Customer</div>
              {customer ? (
                <div className="flex items-center justify-between p-3 rounded-xl bg-[#1F5EFF]/10 border border-[#1F5EFF]/35" data-testid={`${kind}-create-customer`}>
                  <div>
                    <div className="text-white text-sm font-medium">{customer.first_name} {customer.last_name}</div>
                    <div className="text-white/40 text-xs">{customer.phone}{customer.email ? ` · ${customer.email}` : ""}</div>
                  </div>
                  <button onClick={() => { setCustomer(null); setCustomerId(""); setVehicleId(""); }} className="text-white/40 hover:text-white text-xs" data-testid={`${kind}-customer-change`}>Change</button>
                </div>
              ) : addingCustomer ? (
                <NewCustomerForm
                  onCreated={(c) => { setCustomer(c); setCustomerId(c.id); setVehicleId(""); setAddingCustomer(false); }}
                  onCancel={() => setAddingCustomer(false)}
                />
              ) : (
                <SearchSelect
                  testId={`${kind}-customer`}
                  placeholder="Search name, phone or email…"
                  addLabel="Add new customer"
                  fetchResults={async (q) => (await CRM_API.get(`/customers?q=${encodeURIComponent(q)}&limit=20`)).data}
                  renderItem={(c, pick) => (
                    <button type="button" key={c.id} onClick={() => pick(c)} data-testid={`${kind}-customer-option-${c.id}`}
                      className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/5">
                      {c.first_name} {c.last_name} <span className="text-white/40">· {c.phone}</span>
                    </button>
                  )}
                  onSelect={(c) => { setCustomer(c); setCustomerId(c.id); setVehicleId(""); }}
                  onAddNew={() => setAddingCustomer(true)}
                />
              )}
            </div>

            {/* vehicle (auto-loaded from customer) */}
            <div>
              <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Car className="w-3 h-3" /> Vehicle</div>
              {!customerId ? (
                <div className="text-white/30 text-xs p-3 border border-dashed border-white/10 rounded-lg">Select a customer first — their vehicles load automatically</div>
              ) : addingVehicle ? (
                <NewVehicleForm
                  customerId={customerId}
                  onCreated={(v) => { setCustVehicles((l) => [...l, v]); setVehicleId(v.id); setAddingVehicle(false); }}
                  onCancel={() => setAddingVehicle(false)}
                />
              ) : (
                <div className="space-y-1.5" data-testid={`${kind}-create-vehicle`}>
                  {custVehicles.map((v) => (
                    <button key={v.id} onClick={() => setVehicleId(v.id)} data-testid={`${kind}-vehicle-${v.id}`}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${vehicleId === v.id ? "bg-[#1F5EFF]/15 border-[#1F5EFF] text-white" : "bg-white/5 border-white/8 text-white/70 hover:border-white/25"}`}>
                      <span className="font-medium">{[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}</span>
                      {(v.license_plate || v.vin) && <span className="text-white/35 text-xs ml-2">{[v.license_plate, v.vin].filter(Boolean).join(" · ")}</span>}
                    </button>
                  ))}
                  <button onClick={() => setAddingVehicle(true)} data-testid={`${kind}-vehicle-add-new`}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-dashed border-white/15 hover:border-[#1F5EFF]/50 text-sm text-[#8db2ff] hover:text-white transition-colors">
                    <Plus className="w-3.5 h-3.5 inline mr-1" />Add new vehicle
                  </button>
                </div>
              )}
            </div>

            {/* packages — one click */}
            {(pricingCfg?.bundles || []).filter((b) => b.active).length > 0 && (
              <div>
                <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest mb-2">Packages — one click</div>
                <div className="flex flex-wrap gap-1.5" data-testid={`${kind}-bundles`}>
                  {(pricingCfg.bundles || []).filter((b) => b.active).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)).map((b) => (
                    <button key={b.key} type="button" onClick={() => addBundle(b)} data-testid={`${kind}-bundle-${b.key}`}
                      className="px-3 py-1.5 rounded-lg border border-[#1F5EFF]/40 bg-[#1F5EFF]/10 text-xs text-white/85 hover:bg-[#1F5EFF]/25 transition-colors">
                      {b.name}
                    </button>
                  ))}
                </div>
                <div className="text-white/30 text-[10px] mt-1.5">
                  {promoActive(pricingCfg?.promo)
                    ? "Grand Opening is live: package services get the promo discount (50% off the biggest, 20% off the rest)."
                    : "Package discount is applied to each added line."}
                </div>
              </div>
            )}

            {/* service picker */}
            <div>
              <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Add services from catalog
                {vclass && <span className="text-emerald-300/80 normal-case" data-testid={`${kind}-class-hint`}>· {vclass} pricing (vehicle detected)</span>}
              </div>
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input value={svcQuery} onChange={(e) => setSvcQuery(e.target.value)} placeholder="Search services…"
                  className={inp + " pl-8"} data-testid={`${kind}-service-search`} />
              </div>
              {(() => {
                const fdItem = items.find((i) => i.pkey === "full-detail");
                const du = pricingCfg?.addons?.deep_upgrade;
                if (!fdItem || !du) return null;
                const duPrice = (vclass && du.prices?.[vclass] != null)
                  ? du.prices[vclass]
                  : Math.min(...Object.values(du.prices || {}).filter((v) => v != null));
                const duOn = items.some((i) => i.pkey === "deep_upgrade");
                return (
                  <div className="mb-2 rounded-lg border border-[#1F5EFF]/30 bg-[#1F5EFF]/5 px-3 py-2 flex items-center justify-between gap-3"
                    data-testid={`${kind}-deep-upgrade`}>
                    <div className="text-[11px] text-white/70 leading-snug">
                      <span className="text-white font-medium">Need a deeper clean?</span>{" "}
                      Upgrade to Deep Interior Cleaning for intensive carpet, upholstery and stain treatment.
                    </div>
                    <button type="button" disabled={duOn} data-testid={`${kind}-deep-upgrade-add`}
                      onClick={() => setItems((rows) => [...rows, {
                        service_id: null, name: du.label, qty: 1,
                        unit_price: duPrice, discount: fdItem.discount || 0, pkey: "deep_upgrade",
                      }])}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        duOn ? "bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 cursor-default"
                          : "bg-[#1F5EFF] text-white hover:bg-[#8db2ff] hover:text-black"}`}>
                      {duOn ? "Added ✓" : `+ $${duPrice}`}
                    </button>
                  </div>
                );
              })()}
              <div className="max-h-56 overflow-y-auto overscroll-contain space-y-3 pr-1" data-testid={`${kind}-service-picker`}>
                {Object.entries(svcGroups).map(([cat, list]) => (
                  <div key={cat}>
                    <div className="font-mono-alt text-[9px] text-[#8db2ff] uppercase tracking-widest mb-1">{cat}</div>
                    <div className="space-y-1">
                      {list.map((s) => (
                        <button key={s.id} onClick={() => addService(s)} data-testid={`${kind}-svc-${s.id}`}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/8 hover:border-[#1F5EFF]/50 text-sm group">
                          <span className="text-white/80 group-hover:text-white text-left">{s.name}</span>
                          <span className="text-[#8db2ff] font-mono-alt text-xs ml-3 whitespace-nowrap">${Number(svcPrice(s) || 0).toFixed(0)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(svcGroups).length === 0 && <div className="text-white/30 text-xs p-2">No services match</div>}
              </div>
              <button onClick={() => setItems((r) => [...r, { service_id: null, name: "", qty: 1, unit_price: "", discount: 0 }])}
                className="mt-2 text-[#8db2ff] text-xs hover:text-white inline-flex items-center gap-1" data-testid={`${kind}-item-add`}>
                <Plus className="w-3.5 h-3.5" /> Custom line
              </button>
            </div>
          </div>

          {/* RIGHT: lines + totals */}
          <div className="p-6 space-y-4 lg:overflow-y-auto lg:min-h-0">
            <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest">Line items</div>
            {items.length === 0 && (
              <div className="p-8 text-center border border-dashed border-white/10 rounded-xl text-white/35 text-sm">
                Pick services from the catalog on the left
              </div>
            )}
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/10 space-y-2" data-testid={`${kind}-line-${i}`}>
                  <div className="flex items-center gap-2">
                    <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Service / item"
                      className={`${inp} flex-1`} data-testid={`${kind}-item-name-${i}`} />
                    <button onClick={() => setItems((r) => r.filter((_, j) => j !== i))} className="p-2 text-white/40 hover:text-red-400" data-testid={`${kind}-item-del-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-white/40 text-[10px] font-mono-alt uppercase">Qty
                      <input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className={`${inp} w-16`} data-testid={`${kind}-item-qty-${i}`} /></label>
                    <label className="flex items-center gap-1.5 text-white/40 text-[10px] font-mono-alt uppercase">Price $
                      <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className={`${inp} w-24`} data-testid={`${kind}-item-price-${i}`} /></label>
                    <label className="flex items-center gap-1.5 text-white/40 text-[10px] font-mono-alt uppercase">Disc
                      <input type="number" min="0" max="100" step="1"
                        value={it.discount_pct ?? (Number(it.unit_price) > 0 && Number(it.discount) > 0
                          ? Math.round(Number(it.discount) / (Number(it.unit_price) * Number(it.qty || 1)) * 100) : 0)}
                        onChange={(e) => {
                          const pct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          const disc = Math.round(Number(it.unit_price || 0) * Number(it.qty || 1) * pct) / 100;
                          setItems((r) => r.map((x, j) => (j === i ? { ...x, discount_pct: pct, discount: disc } : x)));
                        }}
                        className={`${inp} !w-14 !px-2 text-center`} data-testid={`${kind}-item-disc-${i}`} />
                      <span className="text-white/40 text-[10px] font-mono-alt uppercase">%</span>
                      <span className="text-emerald-300/80 text-[11px] tabular-nums whitespace-nowrap">−${(Number(it.discount) || 0).toFixed(2)}</span>
                    </label>
                    <span className="ml-auto text-[#8db2ff] font-display text-sm tabular-nums">
                      ${(Math.max(0, Number(it.qty || 1) * Number(it.unit_price || 0) - Number(it.discount || 0))).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25 space-y-1.5" data-testid={`${kind}-totals`}>
              <div className="flex justify-between text-white/50 text-xs"><span>Subtotal</span><span className="tabular-nums">${totals.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between items-center text-white/50 text-xs">
                <span>Discount $</span>
                <input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className={`${inp} !w-24 !py-1 text-right`} data-testid={`${kind}-create-discount`} />
              </div>
              <div className="flex justify-between text-white/50 text-xs"><span>GST (5%)</span><span className="tabular-nums">${totals.tax.toFixed(2)}</span></div>
              <div className="flex justify-between pt-1.5 border-t border-white/10"><span className="text-white text-sm font-medium">Total</span><span className="font-display text-lg text-[#8db2ff] tabular-nums">${totals.total.toFixed(2)}</span></div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button onClick={save} disabled={busy} className="flex-1 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium transition-colors disabled:opacity-40" data-testid={`${kind}-create-save`}>
                {busy ? "…" : `Create ${kind === "estimate" ? "Estimate" : "Invoice"}`}
              </button>
              <button onClick={onClose} className="px-4 py-3 text-white/50 text-sm hover:text-white" data-testid={`${kind}-create-cancel`}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================== ESTIMATES ==================== */
export function EstimatesList() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [params] = useSearchParams();
  const load = () => CRM_API.get("/estimates").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  // Quick-create from a customer page: /crm/estimates?new=1&customer=<id>
  useEffect(() => { if (params.get("new") === "1") setCreating(true); }, [params]);
  return (
    <div data-testid="crm-est-list">
      <PageHeader eyebrow="Billing" title="Estimates" subtitle="Send price quotes and let customers approve online."
        right={(
          <button onClick={() => setCreating(true)} data-testid="btn-new-estimate"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
            <Plus className="w-4 h-4" /> New Estimate
          </button>
        )} />
      <div className="px-6 md:px-10 py-6 space-y-2">
        {rows.length === 0 && <div className="p-12 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">No estimates yet — create one with the button above.</div>}
        {rows.map((e) => (
          <Link key={e.id} to={`/crm/estimates/${e.id}`} data-testid={`est-${e.id}`} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/40">
            <FileText className="w-4 h-4 text-[#1F5EFF]" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">{e.est_number}</div>
              <div className="text-white/40 text-xs">{new Date(e.created_at).toLocaleDateString()} · ${e.total?.toFixed(2)}</div>
            </div>
            <span className={`px-2.5 py-1 rounded text-[10px] font-mono-alt uppercase tracking-widest ${EST_STATUS[e.status]}`}>{e.status}</span>
          </Link>
        ))}
      </div>
      {creating && <DocCreateModal kind="estimate"
        prefillCustomerId={params.get("customer") || ""}
        prefillVehicleId={params.get("vehicle") || ""}
        prefillServiceIds={(params.get("services") || "").split(",").filter(Boolean)}
        prefillWorkOrderId={params.get("wo") || ""}
        onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

export function EstimateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => CRM_API.get(`/estimates/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-10 text-white/40">Loading…</div>;
  const e = data.estimate;
  const editable = e.status === "draft";
  const sendIt = async () => { setBusy(true); try { const { data } = await CRM_API.post(`/estimates/${id}/send`); if (data.notify?.ok) toast.success("Estimate emailed to the customer"); else if (data.notify?.ok === false) toast.error(`Email failed: ${data.notify.error || "unknown"}`); else toast.success("Marked sent — no customer email, share the link below"); await load(); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); } };

  const publicUrl = `${window.location.origin}/estimate/${e.secure_token || ""}`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };

  // Customer-ready quote text — order is never modified by copying
  const copyQuote = async () => {
    const lines = e.items.map((it) => ({ label: it.name, amount: it.qty * it.unit_price - (it.discount || 0) }));
    const fv = e.items.some((it) => /full vehicle ppf/i.test(it.name || ""));
    const text = buildQuoteText({
      vehicleLabel: [data.vehicle?.year, data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(" "),
      lines, subtotal: e.subtotal, discount: e.discount_total || 0,
      taxRate: e.tax_rate, tax: e.tax_total, total: e.total,
      fullVehiclePreliminary: fv,
    });
    const ok = await copyText(text);
    if (ok) toast.success("Copied!");
    else toast.error("Copy failed — clipboard blocked by the browser");
  };

  const convertToInvoice = async () => {
    try {
      const { data: inv } = await CRM_API.post("/invoices", {
        customer_id: e.customer_id, vehicle_id: e.vehicle_id, estimate_id: id,
        items: e.items, tax_rate: e.tax_rate, discount_total: e.discount_total,
      });
      toast.success(`Invoice ${inv.invoice_number} created`);
      nav(`/crm/invoices/${inv.id}`);
    } catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <div data-testid="crm-est-detail">
      <PageHeader
        eyebrow={e.est_number} title={`${data.customer?.first_name || ""} ${data.customer?.last_name || ""}`.trim()}
        subtitle={[data.vehicle?.year, data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(" ")}
        right={<span className={`px-3 py-1.5 rounded text-[10px] font-mono-alt uppercase tracking-widest ${EST_STATUS[e.status]}`}>{e.status}</span>}
      />
      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="space-y-3 text-sm">
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
            {e.status === "draft" && <button onClick={sendIt} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium disabled:opacity-40" data-testid="est-send"><Send className="w-4 h-4" />Send to customer</button>}
            <button onClick={() => downloadPdf("estimates", id, e.est_number)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm" data-testid="est-pdf">
              <Download className="w-4 h-4" /> Download PDF
            </button>
            <button onClick={copyQuote} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm" data-testid="est-copy-quote">
              <Copy className="w-4 h-4" /> Copy Quote
            </button>
            {e.status !== "draft" && e.secure_token && (
              <button onClick={copyLink} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm" data-testid="est-copy">
                <Copy className="w-4 h-4" /> Copy customer link
              </button>
            )}
            {e.status === "approved" && (
              <button onClick={convertToInvoice} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/25 hover:bg-emerald-500 text-emerald-200 hover:text-white text-sm font-medium" data-testid="est-to-invoice">
                <Receipt className="w-4 h-4" /> Convert to Invoice
              </button>
            )}
          </div>
          <InfoRow label="Subtotal" value={`$${e.subtotal?.toFixed(2)}`} />
          <InfoRow label="Discount" value={`$${(e.discount_total || 0).toFixed(2)}`} />
          <InfoRow label={`Tax (${e.tax_rate}%)`} value={`$${e.tax_total?.toFixed(2)}`} />
          <InfoRow label="Total" value={<span className="text-lg font-display text-[#8db2ff]">${e.total?.toFixed(2)}</span>} />
          {e.expires_at && <InfoRow label="Expires" value={new Date(e.expires_at).toLocaleDateString()} />}
          {e.customer_response && <InfoRow label="Response" value={`${e.customer_response.name || "—"} · ${new Date(e.customer_response.at).toLocaleDateString()}`} />}
        </aside>
        <section className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-6 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Line items</span>
          </div>
          <div className="space-y-2 mb-4">
            {e.items.map((it, i) => {
              const line = it.qty * it.unit_price - (it.discount || 0);
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex-1">
                    <div className="text-white text-sm">{it.name}</div>
                    {it.description && <div className="text-white/40 text-xs">{it.description}</div>}
                  </div>
                  <div className="text-white/50 text-xs">
                    {it.qty} × ${it.unit_price}
                    {Number(it.discount) > 0 && (
                      <span className="text-emerald-300/80"> −${Number(it.discount).toFixed(2)}{it.discount_pct ? ` (${it.discount_pct}%)` : ""}</span>
                    )}
                  </div>
                  <div className="font-display text-[#8db2ff] tabular-nums w-20 text-right">${line.toFixed(2)}</div>
                </div>
              );
            })}
          </div>
          {e.status !== "approved" && e.status !== "declined" && (
            <PpfUpgradesPanel kind="estimates" docId={id} items={e.items} onChanged={load} />
          )}
          {editable && <EstimateEditor key={JSON.stringify(e.items.map((i) => [i.name, i.unit_price]))} initial={e} onSaved={load} />}
        </section>
      </div>
    </div>
  );
}

function EstimateEditor({ initial, onSaved }) {
  const [items, setItems] = useState(initial.items || []);
  const [discount, setDiscount] = useState(initial.discount_total || 0);
  const [taxRate, setTaxRate] = useState(initial.tax_rate || 5);
  const [busy, setBusy] = useState(false);

  const totals = (() => {
    const subtotal = items.reduce((acc, it) => acc + Math.max(0, (it.qty * it.unit_price) - (it.discount || 0)), 0);
    const after = Math.max(0, subtotal - discount);
    const tax = after * (taxRate / 100);
    return { subtotal, tax, total: after + tax };
  })();

  const save = async () => {
    setBusy(true);
    try { await CRM_API.patch(`/estimates/${initial.id}`, { items, discount_total: Number(discount), tax_rate: Number(taxRate) }); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25" data-testid="est-editor">
      <div className="font-mono-alt text-[10px] text-[#8db2ff] uppercase tracking-widest mb-3">Edit line items</div>
      <div className="space-y-2 mb-4">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2">
            <input value={it.name} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Service" className={inp + " col-span-5"} />
            <input type="number" value={it.qty} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x))} className={inp + " col-span-2"} />
            <input type="number" value={it.unit_price} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x))} className={inp + " col-span-2"} />
            <div className={inp + " col-span-2 flex items-center gap-1 !px-2"} title="Line discount, %">
              <input type="number" min="0" max="100" step="1" placeholder="0"
                value={it.discount_pct ?? (Number(it.unit_price) > 0 && Number(it.discount) > 0
                  ? Math.round(Number(it.discount) / (Number(it.unit_price) * Number(it.qty || 1)) * 100) : 0)}
                onChange={(e) => {
                  const pct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                  const disc = Math.round(Number(it.unit_price || 0) * Number(it.qty || 1) * pct) / 100;
                  setItems(items.map((x, j) => (j === i ? { ...x, discount_pct: pct, discount: disc } : x)));
                }}
                className="w-full bg-transparent outline-none text-center" data-testid={`est-item-disc-${i}`} />
              <span className="text-white/40 text-xs">%</span>
            </div>
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="col-span-1 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setItems([...items, { name: "New item", qty: 1, unit_price: 0, discount: 0 }])} className="mb-4 inline-flex items-center gap-1 text-sm text-[#8db2ff]"><Plus className="w-3.5 h-3.5" />Add line</button>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="block"><div className="text-[10px] font-mono-alt text-white/40 mb-1 tracking-widest uppercase">Discount ($)</div><input type="number" value={discount} onChange={(e) => setDiscount(Number(e.target.value))} className={inp} /></label>
        <label className="block"><div className="text-[10px] font-mono-alt text-white/40 mb-1 tracking-widest uppercase">Tax rate (%)</div><input type="number" step="0.1" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value))} className={inp} /></label>
      </div>
      <div className="text-white/60 text-sm mb-3">Subtotal ${totals.subtotal.toFixed(2)} · Tax ${totals.tax.toFixed(2)} · <span className="text-white">Total ${totals.total.toFixed(2)}</span></div>
      <button onClick={save} disabled={busy} className="w-full py-2.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium disabled:opacity-40" data-testid="est-save">
        {busy ? "…" : "Save changes"}
      </button>
    </div>
  );
}

/* ==================== INVOICES ==================== */
export function InvoicesList() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [params] = useSearchParams();
  const load = () => CRM_API.get("/invoices").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new") === "1") setCreating(true); }, [params]);
  return (
    <div data-testid="crm-inv-list">
      <PageHeader eyebrow="Billing" title="Invoices" subtitle="Track balances, payments and outstanding amounts."
        right={(
          <button onClick={() => setCreating(true)} data-testid="btn-new-invoice"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
            <Plus className="w-4 h-4" /> New Invoice
          </button>
        )} />
      <div className="px-6 md:px-10 py-6 space-y-2">
        {rows.length === 0 && <div className="p-12 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">No invoices yet — create one with the button above.</div>}
        {rows.map((i) => (
          <Link key={i.id} to={`/crm/invoices/${i.id}`} data-testid={`inv-${i.id}`} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/40">
            <Receipt className="w-4 h-4 text-[#1F5EFF]" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">{i.invoice_number}</div>
              <div className="text-white/40 text-xs">Total ${i.total?.toFixed(2)} · Paid ${(i.amount_paid || 0).toFixed(2)} · Balance ${i.balance_due?.toFixed(2)}</div>
            </div>
            <span className={`px-2.5 py-1 rounded text-[10px] font-mono-alt uppercase tracking-widest ${INV_STATUS[i.status]}`}>{i.status}</span>
          </Link>
        ))}
      </div>
      {creating && <DocCreateModal kind="invoice"
        prefillCustomerId={params.get("customer") || ""}
        prefillVehicleId={params.get("vehicle") || ""}
        prefillServiceIds={(params.get("services") || "").split(",").filter(Boolean)}
        prefillWorkOrderId={params.get("wo") || ""}
        onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

export function InvoiceDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);

  const load = () => CRM_API.get(`/invoices/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);

  const addPayment = async () => {
    const amount = Number(payAmt); if (!amount || amount <= 0) { toast.error("Amount required"); return; }
    setBusy(true);
    try { await CRM_API.post("/payments", { invoice_id: id, method, amount }); toast.success("Payment recorded"); setPayAmt(""); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendIt = async () => { try { const { data } = await CRM_API.post(`/invoices/${id}/send`); if (data.notify?.ok) toast.success("Invoice emailed to the customer"); else if (data.notify?.ok === false) toast.error(`Email failed: ${data.notify.error || "unknown"}`); else toast.success("Marked sent — no customer email on file"); await load(); } catch (e) { toast.error(errMsg(e)); } };

  const generateSquare = async () => {
    try {
      const { data: link } = await CRM_API.post("/square/payment-link", { invoice_id: id, kind: "final" });
      toast.success("Payment link ready");
      navigator.clipboard.writeText(`${window.location.origin}${link.url}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="p-10 text-white/40">Loading…</div>;
  const inv = data.invoice;

  // Customer-ready quote text — never modifies the order or its payments
  const copyQuote = async () => {
    const lines = inv.items.map((it) => ({ label: it.name, amount: it.qty * it.unit_price - (it.discount || 0) }));
    const fv = inv.items.some((it) => /full vehicle ppf/i.test(it.name || ""));
    const text = buildQuoteText({
      vehicleLabel: [data.vehicle?.year, data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(" "),
      lines, subtotal: inv.subtotal, discount: inv.discount_total || 0,
      taxRate: inv.tax_rate, tax: inv.tax_total, total: inv.total,
      paid: inv.amount_paid || 0, balance: inv.balance_due,
      fullVehiclePreliminary: fv,
    });
    const ok = await copyText(text);
    if (ok) toast.success("Copied!");
    else toast.error("Copy failed — clipboard blocked by the browser");
  };

  return (
    <div data-testid="crm-inv-detail">
      <PageHeader
        eyebrow={inv.invoice_number}
        title={`${data.customer?.first_name || ""} ${data.customer?.last_name || ""}`.trim()}
        subtitle={[data.vehicle?.year, data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(" ")}
        right={<span className={`px-3 py-1.5 rounded text-[10px] font-mono-alt uppercase tracking-widest ${INV_STATUS[inv.status]}`}>{inv.status}</span>}
      />
      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="space-y-3 text-sm">
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
            {inv.status === "draft" && <button onClick={sendIt} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium" data-testid="inv-send"><Send className="w-4 h-4" />Send to customer</button>}
            <button onClick={() => downloadPdf("invoices", id, inv.invoice_number)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm" data-testid="inv-pdf">
              <Download className="w-4 h-4" /> Download PDF
            </button>
            <button onClick={copyQuote} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm" data-testid="inv-copy-quote">
              <Copy className="w-4 h-4" /> Copy Quote
            </button>
            <button onClick={generateSquare} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm" data-testid="inv-square"><DollarSign className="w-4 h-4" />Square link (stub)</button>
          </div>
          <InfoRow label="Total" value={<span className="text-lg font-display text-[#8db2ff]">${inv.total?.toFixed(2)}</span>} />
          <InfoRow label="Paid" value={`$${(inv.amount_paid || 0).toFixed(2)}`} />
          <InfoRow label="Balance" value={<span className="text-lg font-display text-white">${inv.balance_due?.toFixed(2)}</span>} />
          <InfoRow label={`Tax (${inv.tax_rate}%)`} value={`$${inv.tax_total?.toFixed(2)}`} />
          {inv.paid_at && <InfoRow label="Paid at" value={new Date(inv.paid_at).toLocaleString()} />}
        </aside>
        <section className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-3"><span className="w-6 h-px bg-[#1F5EFF]" /><span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Line items</span></div>
            <div className="space-y-2">
              {inv.items.map((it, i) => {
                const line = it.qty * it.unit_price - (it.discount || 0);
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex-1">
                      <div className="text-white text-sm">{it.name}</div>
                    </div>
                    <div className="text-white/50 text-xs">
                    {it.qty} × ${it.unit_price}
                    {Number(it.discount) > 0 && (
                      <span className="text-emerald-300/80"> −${Number(it.discount).toFixed(2)}{it.discount_pct ? ` (${it.discount_pct}%)` : ""}</span>
                    )}
                  </div>
                    <div className="font-display text-[#8db2ff] tabular-nums w-20 text-right">${line.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {inv.status !== "cancelled" && (
            <PpfUpgradesPanel kind="invoices" docId={id} items={inv.items} onChanged={load} />
          )}

          <div>
            <div className="flex items-center gap-3 mb-3"><span className="w-6 h-px bg-[#1F5EFF]" /><span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Payments</span></div>
            <div className="space-y-2 mb-3">
              {data.payments.length === 0 ? <div className="text-white/40 text-sm italic">No payments yet.</div> :
                data.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <div className="flex-1">
                      <div className="text-white text-sm">${p.amount.toFixed(2)}</div>
                      <div className="text-white/40 text-xs">{p.method.replace(/_/g, " ")} · {new Date(p.received_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              }
            </div>
            {inv.balance_due > 0 && (
              <div className="p-4 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25 flex gap-2" data-testid="inv-add-payment">
                <input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder={`Balance $${inv.balance_due.toFixed(2)}`} className={inp} />
                <select value={method} onChange={(e) => setMethod(e.target.value)} className={inp + " w-40"}>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="e_transfer">E-transfer</option>
                  <option value="square">Square</option>
                  <option value="other">Other</option>
                </select>
                <button onClick={addPayment} disabled={busy} className="px-5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium disabled:opacity-40 whitespace-nowrap" data-testid="inv-pay-submit">
                  Record
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5 border border-white/8">
    <div className="text-[11px] font-mono-alt text-white/40 uppercase tracking-widest">{label}</div>
    <div className="text-white text-sm text-right">{value}</div>
  </div>
);
